import React, { useEffect, useState } from 'react';
import {
  getEditorSettings,
  saveEditorSettings,
  type EditorSettings,
} from '../../systems/editor-settings';
import styles from './scenario-studio.module.css';

interface StudioSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: (settings: EditorSettings) => void;
}

export const StudioSettingsModal: React.FC<StudioSettingsModalProps> = ({
  isOpen,
  onClose,
  onSaved,
}) => {
  const [settings, setSettings] = useState<EditorSettings>({
    anthropicApiKey: '',
    anthropicModel: '',
    lightTheme: false,
    autoSave: true,
  });
  const [showKey, setShowKey] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSettings(getEditorSettings());
    setSaved(false);
    setShowKey(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSave = () => {
    saveEditorSettings(settings);
    onSaved?.(settings);
    setSaved(true);
    onClose();
  };

  const hasKey = Boolean(settings.anthropicApiKey.trim());

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>Paramètres de l&apos;éditeur</h2>
          <button type="button" className={styles.btnGhost} onClick={onClose}>
            ✕
          </button>
        </div>

        <div className={styles.modalBody}>
          <section className={styles.settingsSection}>
            <h3>Apparence</h3>
            <p className={styles.settingsHint}>
              Thème clair avec les mêmes accents bleus — enregistré dans ce navigateur.
            </p>
            <label className={styles.settingsToggle}>
              <input
                type="checkbox"
                checked={settings.lightTheme === true}
                onChange={(e) =>
                  setSettings((prev) => ({ ...prev, lightTheme: e.target.checked }))
                }
              />
              <span>Thème clair</span>
            </label>
          </section>

          <section className={styles.settingsSection}>
            <h3>Enregistrement</h3>
            <p className={styles.settingsHint}>
              Sauvegarde automatique toutes les 10 minutes lorsque des modifications non
              enregistrées existent.
            </p>
            <label className={styles.settingsToggle}>
              <input
                type="checkbox"
                checked={settings.autoSave !== false}
                onChange={(e) =>
                  setSettings((prev) => ({ ...prev, autoSave: e.target.checked }))
                }
              />
              <span>Enregistrement automatique (10 min)</span>
            </label>
          </section>

          <section className={styles.settingsSection}>
            <h3>Génération IA (Anthropic)</h3>
            <p className={styles.settingsHint}>
              {hasKey
                ? 'Clé API configurée dans ce navigateur.'
                : 'Aucune clé locale — le serveur utilisera ANTHROPIC_API_KEY si définie dans .env.'}
            </p>

            <label className={styles.settingsLabel}>
              Clé API Anthropic
              <div className={styles.settingsKeyRow}>
                <input
                  className={styles.input}
                  type={showKey ? 'text' : 'password'}
                  placeholder="sk-ant-…"
                  value={settings.anthropicApiKey}
                  onChange={(e) =>
                    setSettings((prev) => ({ ...prev, anthropicApiKey: e.target.value }))
                  }
                  autoComplete="off"
                />
                <button
                  type="button"
                  className={styles.btnSecondary}
                  onClick={() => setShowKey((v) => !v)}
                >
                  {showKey ? 'Masquer' : 'Afficher'}
                </button>
              </div>
            </label>

            <label className={styles.settingsLabel}>
              Modèle (optionnel)
              <input
                className={styles.input}
                type="text"
                placeholder="claude-sonnet-5"
                value={settings.anthropicModel ?? ''}
                onChange={(e) =>
                  setSettings((prev) => ({ ...prev, anthropicModel: e.target.value }))
                }
              />
            </label>

            {saved && (
              <p className={styles.settingsSaved} role="status">
                Paramètres enregistrés.
              </p>
            )}
          </section>
        </div>

        <div className={styles.modalFooter}>
          <button type="button" className={styles.btnGhost} onClick={onClose}>
            Annuler
          </button>
          <button type="button" className={styles.btnPrimary} onClick={handleSave}>
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};
